import {TmcCodeResponse, TmcLeg} from "../services/models/TmcRequests";
import {OptionsTicket, TmcTicket} from "../types/OptionsTicket";
import {distinct} from "../../../../js/util/UtilFunctions";
import {BaseStrategy, Strategy, WhatIf} from "../types/WhatIf";
import {SecurityUtil} from "../../../../js/util/SecurityUtil";
import {OrderSide} from "../../../../orders/models/OrderRecord";

export class OrderTicketUtil {

    public static tmcLegs(whatIf: BaseStrategy): Array<TmcLeg> {
        if (this.isStrategy(whatIf)) {
            return whatIf.trades.map(tr => OrderTicketUtil.buildLeg(tr));
        } else if (this.isWhatIf(whatIf)) {
            return whatIf.trades.map(tr => OrderTicketUtil.buildLeg(tr));
        } else {
            throw Error("Unknown Strategy Type");
        }
    }

    private static scaleFactor(strategy: BaseStrategy): number {
        if (OrderTicketUtil.isStrategy(strategy)) {
            return strategy.scaleFactor;
        } else if (OrderTicketUtil.isWhatIf(strategy)) {
            return strategy.lots;
        } else {
            throw Error("Unknown Strategy Type");
        }
    }

    public static isStrategy(value: BaseStrategy): value is Strategy {
        return "scaleFactor" in value;
    }

    public static isWhatIf(value: BaseStrategy): value is WhatIf {
        return "lots" in value;
    }

    private static buildLeg(trade: BaseTrade): TmcLeg {
        return {
            code: trade.code,
            ratio: trade.quantity
        };
    }

    /**
     * Assumes that the Trades have already been validated by the server
     */
    public static buildTmc(tmc: TmcCodeResponse, whatIf: BaseStrategy): TmcTicket {
        OrderTicketUtil.ensureSingleUnderlying(whatIf.trades);

        const orderQuantity = OrderTicketUtil.scaleFactor(whatIf);
        const trades = OrderTicketUtil.tmcLegs(whatIf);
        const side = tmc.opposite ? OrderSide.MULTI_OPPOSITE : OrderSide.MULTI_AS_DEFINED;
        return {
            code: tmc.code,
            quantity: orderQuantity,
            description: whatIf.name,
            opposite: tmc.opposite,
            legs: trades,
            buy: side === OrderSide.MULTI_AS_DEFINED
        };
    }

    public static underlyingFor(trades: Array<BaseTrade>): string {
        const underlyings = trades.map(tr => tr.code)
                                  .map(code => SecurityUtil.underlying(code))
                                  .filter(distinct);
        if (underlyings.length === 1) {
            return underlyings[0];
        } else {
            throw Error("Multiple underlyings");
        }
    }

    public static ensureSingleUnderlying(trades: Array<BaseTrade>) {
        OrderTicketUtil.underlyingFor(trades);
    }

    public static buildTicket(trade: BaseTrade): OptionsTicket {
        const side = OrderTicketUtil.side(trade.quantity);

        return {
            code: trade.code,
            quantity: trade.quantity,
            description: trade.code,
            buy: side === OrderSide.BUY,
            legs: []
        };
    }

    public static side(quantity: number): OrderSide {
        return quantity > 0 ? OrderSide.BUY : OrderSide.LONG_SELL;
    }
}
